import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Empty,
  Typography,
  Button,
  Space,
  Alert,
  message,
  Progress,
  Segmented,
  Collapse,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  CloudDownloadOutlined,
  SaveOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
  EyeOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import {
  savePostCreativeAsset,
  saveActivityPreferenceAsset,
  saveActivityPreference,
  deleteActivityPreference,
  deleteActivityPreferenceAsset,
} from "../services/knowledgeStore";
import NeuroReport from "./NeuroReport";

const { Paragraph, Text, Title } = Typography;

const scoreColor = (score) => {
  if (score >= 8) return "#16a34a"; // green
  if (score >= 6) return "#ca8a04"; // amber
  return "#dc2626"; // red
};

// Renders the creative-director score detail (assessment, suggestions, 10/10 brief).
const CreativeScoreDetail = ({ detail }) => {
  if (!detail) return null;
  const suggestions = Array.isArray(detail.suggestions) ? detail.suggestions : [];
  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      {detail.assessment ? (
        <Text style={{ fontSize: 13 }}>{detail.assessment}</Text>
      ) : null}
      {suggestions.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 12 }}>
            Suggestions
          </Text>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {suggestions.map((s, i) => (
              <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                <Text strong>{s.area || "Fix"}: </Text>
                <Text type="secondary">{s.fix || s.issue || ""}</Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {detail.ten_out_of_ten ? (
        <div>
          <Text strong style={{ fontSize: 12 }}>
            10/10 direction
          </Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            {detail.ten_out_of_ten}
          </Paragraph>
        </div>
      ) : null}
    </Space>
  );
};
const DrivePlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 -960 960 960"
    width="20"
    height="20"
    aria-hidden="true"
    focusable="false"
    fill="currentColor"
  >
    <path d="M232-120q-17,0-31.5-8.5t-22.29-22.09L80.79-320.41Q73-334 73-351t8-31L329-809q8-14 22.5-22.5t31.06-8.5H577.44q16.56,0 31.06,8.5t22.42,22.37L811-500q-21-5-42-4.5T727-500L571-768H389L146-351l92,159H575q11,21.17 25.5,39.59T634-120H232Zm68-171l-27-48L445.95-641H514L624-449q-14.32,13-26.53,28.5T576-388L480-556L369-362H565q-6,17-9.5,34.7T552-291H300ZM732-144V-252H624v-72H732V-432h72v108H912v72H804v108H732Z" />
  </svg>
);

function VariantsGrid({
  variants,
  generationStatus,
  onAnalyzeVariant,
  columns = 3,
  onColumnsChange,
  qualityControlLevel = "off",
  onQualityControlLevelChange,
  adCopyCreatives = [],
  onAdCopyCreativesChange,
  postCreatives = [],
  onPostCreativesChange,
  activityPreferences = [],
  onActivityPreferencesChange,
}) {
  const [now, setNow] = useState(Date.now());
  const [previewVariant, setPreviewVariant] = useState(null);
  const [likedIds, setLikedIds] = useState(new Set());
  const [dislikedIds, setDislikedIds] = useState(new Set());
  const [isCompactMobile, setIsCompactMobile] = useState(false);
  const [mobileMode, setMobileMode] = useState("grid");
  const [mobileDeckIndex, setMobileDeckIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const touchStartXRef = useRef(null);
  const lastCompletedRef = useRef(0);
  const lastCompletedAtRef = useRef(Date.now());
  const lastProgressTickRef = useRef(Date.now());
  const lastDisplayPercentRef = useRef(0);
  const progressMetaRef = useRef({ stage: null, startedAt: null, total: 0 });

  const normalizedPreferences = Array.isArray(activityPreferences)
    ? activityPreferences
    : [];
  const refreshPreferenceSets = (prefs = normalizedPreferences) => {
    const likes = new Set(
      prefs.filter((item) => item.sentiment === "like").map((item) => item.id),
    );
    const dislikes = new Set(
      prefs
        .filter((item) => item.sentiment === "dislike")
        .map((item) => item.id),
    );
    setLikedIds(likes);
    setDislikedIds(dislikes);
  };
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    refreshPreferenceSets();
  }, [normalizedPreferences]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyViewportState = () => {
      const mobile = window.innerWidth <= 768;
      setIsCompactMobile(mobile);
      if (mobile) {
        setMobileMode((prev) => (prev === "rolling" ? prev : "rolling"));
      }
    };
    applyViewportState();
    window.addEventListener("resize", applyViewportState);
    return () => window.removeEventListener("resize", applyViewportState);
  }, []);

  const visibleVariants = useMemo(
    () =>
      (Array.isArray(variants) ? variants : []).filter(
        (variant) => !dismissedIds.has(variant.id),
      ),
    [variants, dismissedIds],
  );

  const mobileDeck = useMemo(() => {
    if (!isCompactMobile) return [];
    return visibleVariants.filter((variant) => {
      const primaryVideoUrl = variant.stitchedVideoUrl || variant.videoUrl;
      return Boolean(primaryVideoUrl || variant.imageUrl || variant.imageStatus === "error");
    });
  }, [visibleVariants, isCompactMobile]);

  useEffect(() => {
    if (mobileDeck.length === 0) {
      setMobileDeckIndex(0);
      return;
    }
    setMobileDeckIndex((prev) => Math.min(prev, mobileDeck.length - 1));
  }, [mobileDeck]);

  const handleDownload = async (variant) => {
    const isVideo =
      variant.mediaType === "video" ||
      variant.stitchedVideoUrl ||
      variant.videoUrl;
    const mediaUrl =
      variant.stitchedVideoUrl || variant.videoUrl || variant.imageUrl;
    if (!mediaUrl) {
      message.error("No media available to download.");
      return;
    }

    const extension =
      variant.mimeType?.includes("video") || isVideo ? "mp4" : "png";
    const filename = `variant-${variant.id || "media"}.${extension}`;
    const href = mediaUrl;

    if (href.startsWith("data:")) {
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = filename;
      anchor.click();
      return;
    }

    try {
      const response = await fetch(href, { mode: "cors" });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.open(href, "_blank");
    }
  };

  const fetchBlobWithRetry = async (url, maxAttempts = 3) => {
    const target = String(url || "").trim();
    if (!target) return null;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await fetch(target);
        if (!response.ok) {
          throw new Error(`Failed to load creative (${response.status})`);
        }
        return await response.blob();
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
        }
      }
    }
    if (target.startsWith("blob:")) {
      throw new Error(
        "Generated media is no longer available in browser memory. Regenerate it and try liking it again.",
      );
    }
    throw lastError || new Error("Failed to load creative");
  };

  const persistPreferenceMedia = async (variant, safeId) => {
    const mediaUrl =
      variant.stitchedVideoUrl || variant.imageUrl || variant.videoUrl || "";
    const mediaType =
      variant.mediaType ||
      (variant.stitchedVideoUrl || variant.videoUrl ? "video" : "image");
    const blob =
      variant?.blob instanceof Blob
        ? variant.blob
        : await fetchBlobWithRetry(mediaUrl);
    if (!blob) {
      throw new Error("Missing creative media for preference save");
    }
    const fallbackMimeType = mediaType === "video" ? "video/mp4" : "image/png";
    const mimeType = blob.type || fallbackMimeType;
    const extension = mediaType === "video" ? "mp4" : "png";
    const title = String(variant.title || "variant").trim() || "variant";
    const stored = await saveActivityPreferenceAsset({
      id: safeId,
      name: `${title}.${extension}`,
      blob,
      mimeType,
    });
    if (!stored?.path) {
      throw new Error("Upload completed but no storage path was returned.");
    }
    return {
      mediaUrl: stored?.url || "",
      path: stored.path,
      signedAt: stored?.signedAt || new Date().toISOString(),
      mimeType: stored?.mimeType || mimeType,
      mediaType,
    };
  };

  const savePreference = async (variant, sentiment) => {
    const safeId = variant.id || `pref-${Date.now()}`;
    try {
      const already = normalizedPreferences.find((item) => item.id === safeId);
      if (already && already.sentiment === sentiment) {
        await deleteActivityPreference(safeId).catch(() => {});
        if (already?.path) {
          await deleteActivityPreferenceAsset(safeId).catch(() => {});
        }
        const next = normalizedPreferences.filter((item) => item.id !== safeId);
        onActivityPreferencesChange?.(next);
        refreshPreferenceSets(next);
        message.success("Preference removed");
        return;
      }
      const media = await persistPreferenceMedia(variant, safeId);
      const record = {
        id: safeId,
        type: "creative_feedback",
        createdAt: new Date().toISOString(),
        sentiment,
        rationale: variant.rationale || "",
        context: variant.segments ? { segments: variant.segments } : variant.context || {},
        mediaUrl: media.mediaUrl,
        path: media.path,
        signedAt: media.signedAt,
        mimeType: media.mimeType,
        mediaType: media.mediaType,
        generationModel: variant.generationModel || "",
        imageModel: variant.imageModel || "",
      };
      await saveActivityPreference(record);
      const next = [
        record,
        ...normalizedPreferences.filter((item) => item.id !== safeId),
      ];
      onActivityPreferencesChange?.(next);
      refreshPreferenceSets(next);
      message.success(sentiment === "like" ? "Saved as liked" : "Saved as disliked");
    } catch (error) {
      const detail = error?.message ? `: ${error.message}` : "";
      message.error(`Failed to save preference${detail}`);
    }
  };

  const handleSaveForAdCopy = (variant) => {
    const next = [
      {
        id: `adcopy-${variant.id || Date.now()}`,
        createdAt: new Date().toISOString(),
        mediaUrl:
          variant.stitchedVideoUrl || variant.imageUrl || variant.videoUrl || "",
        mediaType:
          variant.mediaType ||
          (variant.stitchedVideoUrl || variant.videoUrl ? "video" : "image"),
        context: variant.segments ? { segments: variant.segments } : variant.context || {},
      },
      ...(Array.isArray(adCopyCreatives) ? adCopyCreatives : []),
    ];
    onAdCopyCreativesChange?.(next);
    message.success("Saved to Ad Copy folder");
  };

  const handleSaveForPost = async (variant) => {
    const mediaUrl =
      variant.stitchedVideoUrl || variant.imageUrl || variant.videoUrl || "";
    if (!mediaUrl) {
      message.error("No media available to save.");
      return;
    }
    const mediaType =
      variant.mediaType ||
      (variant.stitchedVideoUrl || variant.videoUrl ? "video" : "image");
    const extension = mediaType === "video" ? "mp4" : "png";
    const name = variant.title || `Saved creative`;
    const id = `post-${variant.id || Date.now()}`;
    const exists = (Array.isArray(postCreatives) ? postCreatives : []).some(
      (item) => item.id === id,
    );
    if (exists) {
      message.info("Already saved to Post");
      return;
    }
    let stored = null;
    try {
      const blob =
        variant?.blob instanceof Blob
          ? variant.blob
          : await fetchBlobWithRetry(mediaUrl);
      if (!blob) {
        throw new Error("Missing creative media");
      }
      stored = await savePostCreativeAsset({
        id,
        name: `${name}.${extension}`,
        blob,
        mimeType: blob.type || (mediaType === "video" ? "video/mp4" : "image/png"),
      });
      if (!stored?.path) {
        throw new Error("Upload completed but no storage path was returned.");
      }
    } catch (error) {
      message.error(error?.message || "Failed to save media");
      return;
    }
    const next = [
      {
        id,
        createdAt: new Date().toISOString(),
        name,
        mediaUrl: stored?.url || "",
        url: stored?.url || "",
        path: stored.path,
        signedAt: stored?.signedAt || new Date().toISOString(),
        mimeType: stored?.mimeType || variant.mimeType || "",
        mediaType,
        context: variant.segments ? { segments: variant.segments } : variant.context || {},
      },
      ...(Array.isArray(postCreatives) ? postCreatives : []),
    ];
    onPostCreativesChange?.(next);
    message.success("Saved to Post");
  };
  const handleSwipeSave = (variant) => {
    if (!variant) return;
    handleSaveForAdCopy(variant);
    setMobileDeckIndex((prev) => Math.min(prev + 1, Math.max(0, mobileDeck.length - 1)));
  };

  const handleSwipeDelete = (variant) => {
    if (!variant) return;
    setDismissedIds((prev) => new Set([...prev, variant.id]));
    message.info("Creative discarded from this session");
  };

  const onDeckTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const onDeckTouchEnd = (event, variant) => {
    if (touchStartXRef.current == null || !variant) return;
    const endX = event.changedTouches?.[0]?.clientX ?? touchStartXRef.current;
    const delta = endX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (delta >= 70) {
      handleSwipeSave(variant);
      return;
    }
    if (delta <= -70) {
      handleSwipeDelete(variant);
    }
  };

  const handleCopyJson = async (variant) => {
    try {
      const json = JSON.stringify(
        variant.segments ? { segments: variant.segments } : variant.context,
        null,
        2,
      );
      await navigator.clipboard.writeText(json);
      message.success("Variant JSON copied to clipboard");
    } catch (err) {
      message.error("Copy failed");
    }
  };

  const showWaiting = generationStatus?.stage === "variants";
  const variantProgress = () => {
    if (generationStatus?.stage !== "variants") return 0;
    const forced = Number.isFinite(generationStatus?.forcePercent)
      ? generationStatus.forcePercent
      : null;
    if (forced !== null) {
      return Math.max(0, Math.min(100, forced));
    }
    const startedAt = generationStatus?.startedAt || now;
    const elapsedSec = Math.max(0, Math.floor((now - startedAt) / 1000));
    const base = Math.min(90, 10 + elapsedSec * 2);
    const creep = Math.max(0, Math.floor((elapsedSec - 40) / 3));
    return Math.min(99, base + creep);
  };

  if (!visibleVariants || visibleVariants.length === 0) {
    return (
      <div>
        {showWaiting && (
          <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text strong>
                {generationStatus?.message || "Generating..."}
              </Text>
              <Progress
                percent={
                  generationStatus?.stage === "variants"
                    ? variantProgress()
                    : generationStatus?.total
                      ? Math.round(
                          ((generationStatus?.completed || 0) /
                            generationStatus?.total) *
                            100,
                        )
                      : 0
                }
                status="active"
              />
            </Space>
          </div>
        )}
        <Empty
          description="No variants generated yet."
          style={{ marginTop: 40 }}
        />
      </div>
    );
  }

  const total = variants.length;
  const doneCount = variants.filter((v) => v.imageStatus === "done").length;
  const errorCount = variants.filter((v) => v.imageStatus === "error").length;
  const pendingCount = variants.filter(
    (v) => v.imageStatus === "pending" || v.imageStatus === "generating",
  ).length;
  const hasVideo = variants.some(
    (v) => v.mediaType === "video" || v.stitchedVideoUrl || v.videoUrl,
  );
  const completedCount = doneCount + errorCount;
  const percent =
    total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const smoothPercent = (() => {
    if (total === 0) return 0;
    if (generationStatus?.stage !== "images") return percent;

    const stage = generationStatus?.stage || "idle";
    const startedAt = generationStatus?.startedAt || 0;
    const meta = progressMetaRef.current;
    if (
      meta.stage !== stage ||
      meta.startedAt !== startedAt ||
      meta.total !== total
    ) {
      meta.stage = stage;
      meta.startedAt = startedAt;
      meta.total = total;
      lastCompletedRef.current = completedCount;
      lastCompletedAtRef.current = now;
      lastProgressTickRef.current = now;
      lastDisplayPercentRef.current = percent;
    }

    let nextPercent = Math.max(lastDisplayPercentRef.current, percent);
    const completedChanged = completedCount !== lastCompletedRef.current;
    if (completedChanged) {
      lastCompletedRef.current = completedCount;
      lastCompletedAtRef.current = now;
      lastProgressTickRef.current = now;
      nextPercent = Math.max(nextPercent, percent);
    } else {
      const elapsedMs = now - lastProgressTickRef.current;
      if (elapsedMs >= 3000) {
        nextPercent = Math.min(99, nextPercent + 1);
        lastProgressTickRef.current = now;
      }
    }

    nextPercent = Math.max(nextPercent, percent);
    if (completedCount >= total && total > 0) {
      nextPercent = 100;
    }
    lastDisplayPercentRef.current = nextPercent;
    return nextPercent;
  })();
  const showImageProgress =
    !showWaiting &&
    (pendingCount > 0 ||
      (completedCount === total &&
        total > 0 &&
        now - lastCompletedAtRef.current < 1200));

  return (
    <div>
      <Space
        align="center"
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Title level={3} style={{ margin: 0 }} className="section-title">
          Variants ({variants.length})
        </Title>
        <Space size={8} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            QC
          </Text>
          <Segmented
            size="small"
            value={qualityControlLevel}
            onChange={(value) => onQualityControlLevelChange?.(value)}
            options={[
              { label: "Off", value: "off" },
              { label: "Basic", value: "basic" },
              { label: "Strict", value: "strict" },
            ]}
          />
          <Segmented
            size="small"
            value={columns}
            onChange={(value) => onColumnsChange?.(value)}
            className="grid-toggle-control"
            options={[
              {
                label: (
                  <span className="grid-toggle" data-count="3" aria-label="3 columns">
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                  </span>
                ),
                value: 3,
              },
              {
                label: (
                  <span className="grid-toggle" data-count="4" aria-label="4 columns">
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                  </span>
                ),
                value: 4,
              },
              {
                label: (
                  <span className="grid-toggle" data-count="5" aria-label="5 columns">
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                    <span className="grid-toggle__bar" />
                  </span>
                ),
                value: 5,
              },
            ]}
          />
        </Space>
      </Space>
      {showWaiting && (
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text strong>
              {generationStatus?.message || "Generating..."}
            </Text>
            <Progress
              percent={
                generationStatus?.stage === "variants"
                  ? variantProgress()
                  : generationStatus?.total
                    ? Math.round(
                        ((generationStatus?.completed || 0) /
                          generationStatus?.total) *
                          100,
                      )
                    : 0
              }
              status="active"
            />
          </Space>
        </div>
      )}
      {showImageProgress && (
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text strong>
              {generationStatus?.message ||
                `${hasVideo ? "Generating videos" : "Generating images"}: ${completedCount}/${total} complete${
                  errorCount > 0 ? ` • ${errorCount} failed` : ""
                }`}
            </Text>
            <Progress percent={smoothPercent} status="active" />
          </Space>
        </div>
      )}
      {isCompactMobile ? (
        <div className="variants-mobile-controls">
          <Segmented
            size="small"
            value={mobileMode}
            onChange={setMobileMode}
            options={[
              { label: "Rolling", value: "rolling" },
              { label: "Grid", value: "grid" },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Swipe right to save • left to discard
          </Text>
        </div>
      ) : null}

      {isCompactMobile && mobileMode === "rolling" ? (
        <div className="mobile-creative-deck-wrap">
          {mobileDeck.length === 0 ? (
            <Alert type="info" showIcon message="No completed creatives yet. Keep generating…" />
          ) : (() => {
              const variant = mobileDeck[mobileDeckIndex];
              const primaryVideoUrl = variant?.stitchedVideoUrl || variant?.videoUrl;
              const hasMedia = Boolean(primaryVideoUrl || variant?.imageUrl);
              return (
                <Card className="panel-card mobile-creative-deck">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Text strong>
                      Creative {mobileDeckIndex + 1} / {mobileDeck.length}
                    </Text>
                    <div
                      className="mobile-creative-deck__media"
                      onTouchStart={onDeckTouchStart}
                      onTouchEnd={(event) => onDeckTouchEnd(event, variant)}
                    >
                      {hasMedia ? (
                        primaryVideoUrl ? (
                          <video src={primaryVideoUrl} controls className="mobile-creative-deck__asset" />
                        ) : (
                          <img src={variant.imageUrl} alt={variant.title || "Creative"} className="mobile-creative-deck__asset" />
                        )
                      ) : (
                        <div className="mobile-creative-deck__empty">Waiting on render…</div>
                      )}
                    </div>
                    <Paragraph style={{ marginBottom: 0 }}>
                      {variant?.rationale || "No rationale provided."}
                    </Paragraph>
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Button danger onClick={() => handleSwipeDelete(variant)}>
                        Discard
                      </Button>
                      <Button type="primary" onClick={() => handleSwipeSave(variant)}>
                        Save
                      </Button>
                    </Space>
                  </Space>
                </Card>
              );
            })()}
        </div>
      ) : (
      <div
        className="variants-grid"
        style={{ "--variant-columns": String(columns) }}
      >
        {visibleVariants.map((variant) => {
          const hasSegments =
            Array.isArray(variant.segments) && variant.segments.length > 0;
          const primaryVideoUrl = variant.stitchedVideoUrl || variant.videoUrl;
          const hasMedia = primaryVideoUrl || variant.imageUrl;
          return (
          <div key={variant.id} className="variants-grid__item">
            <Card
              title={null}
              extra={
                <Space>
                  {hasMedia && onAnalyzeVariant ? (
                    <Button
                      size="small"
                      onClick={() => onAnalyzeVariant?.(variant)}
                    >
                      Add to analysis
                    </Button>
                  ) : null}
                  <Tooltip title="Add to Google Drive">
                    <Button
                      className="drive-icon-button icon-circle-btn"
                      size="small"
                      shape="circle"
                      icon={<DrivePlusIcon />}
                      onClick={() =>
                        message.info(
                          "Google Drive integration isn't configured yet. Connect it in Settings → Integrations.",
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip title="Copy JSON">
                    <Button
                      size="small"
                      shape="circle"
                      className="icon-circle-btn"
                      icon={<FileTextOutlined />}
                      onClick={() => handleCopyJson(variant)}
                    />
                  </Tooltip>
                </Space>
              }
            >
              {hasMedia ? (
                <>
                  <div
                    className="variant-media"
                    style={{
                      width: "100%",
                      aspectRatio:
                        variant.mediaType === "video" || primaryVideoUrl
                          ? "16 / 9"
                          : "1 / 1",
                      background: "#f5f5f5",
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 12,
                      overflow: "hidden",
                      position: "relative",
                    }}
                    onClick={() => setPreviewVariant(variant)}
                  >
                    <div className="variant-media__actions">
                      <Tooltip title="Like">
                        <Button
                          size="small"
                          shape="circle"
                          icon={
                            likedIds.has(variant.id)
                              ? <LikeFilled />
                              : <LikeOutlined />
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            savePreference(variant, "like");
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Dislike">
                        <Button
                          size="small"
                          shape="circle"
                          icon={
                            dislikedIds.has(variant.id)
                              ? <DislikeFilled />
                              : <DislikeOutlined />
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            savePreference(variant, "dislike");
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Save to Ad Copy">
                        <Button
                          size="small"
                          shape="circle"
                          icon={<SaveOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSaveForAdCopy(variant);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Save to Post">
                        <Button
                          size="small"
                          shape="circle"
                          icon={<FileTextOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSaveForPost(variant);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Download">
                        <Button
                          size="small"
                          shape="circle"
                          icon={<CloudDownloadOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDownload(variant);
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div className="variant-media__view">
                      <EyeOutlined style={{ fontSize: 22 }} />
                    </div>
                    {primaryVideoUrl ? (
                      <video
                        src={primaryVideoUrl}
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <img
                        src={variant.imageUrl}
                        alt={variant.title || "Generated variant"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </div>
                  {variant.imageStatus === "error" && (
                    <Alert
                      type="warning"
                      showIcon
                      message={
                        variant.mediaType === "video"
                          ? "Video failed quality checks"
                          : "Image failed quality checks"
                      }
                      description={variant.imageError || "Quality control failed"}
                      style={{ marginBottom: 12 }}
                    />
                  )}
                </>
              ) : variant.imageStatus === "error" ? (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    variant.mediaType === "video"
                      ? "Video generation failed"
                      : "Image generation failed"
                  }
                  description={variant.imageError || "Unknown error"}
                  style={{ marginBottom: 12 }}
                />
              ) : (
                <div
                  className={
                    variant.mediaType === "video"
                      ? "video-loading"
                      : "image-loading"
                  }
                  style={{
                    width: "100%",
                    aspectRatio:
                      variant.mediaType === "video" ? "16 / 9" : "1 / 1",
                    borderRadius: 8,
                    marginBottom: 12,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div className="video-loading__frame" />
                  <div className="video-loading__content">
                    <Text strong>
                      {variant.mediaType === "video"
                        ? "Rendering video"
                        : "Generating image"}
                    </Text>
                    {variant.mediaType === "video" && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Elapsed{" "}
                        {variant.startedAt
                          ? `${Math.floor((now - variant.startedAt) / 1000)}s`
                          : "…"}
                      </Text>
                    )}
                  </div>
                </div>
              )}

              {hasSegments && (
                <Collapse
                  size="small"
                  items={[
                    {
                      key: "segments",
                      label: `Segments (${variant.segments.length})`,
                      children: (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: "100%" }}
                        >
                          {variant.segments.map((segment) => (
                            <div
                              key={`segment-${variant.id}-${segment.batchIndex}`}
                              style={{
                                padding: 10,
                                borderRadius: 6,
                                border: "1px solid rgba(148, 163, 184, 0.2)",
                              }}
                            >
                              <Text strong style={{ fontSize: 12 }}>
                                Batch {segment.batchIndex} ({segment.startSec}s-
                                {segment.endSec}s)
                              </Text>
                              {segment.videoUrl ? (
                                <video
                                  src={segment.videoUrl}
                                  controls
                                  style={{
                                    width: "100%",
                                    marginTop: 8,
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 12, marginTop: 8 }}
                                >
                                  {segment.status === "error"
                                    ? segment.error || "Segment failed"
                                    : "Segment pending..."}
                                </Text>
                              )}
                            </div>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
              )}

              {(variant.neuroReport || variant.neuroReportError) && (
                <NeuroReport
                  report={variant.neuroReport}
                  error={variant.neuroReportError}
                />
              )}

              {variant.scoreStatus === "scoring" ? (
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                  Scoring creative…
                </Text>
              ) : variant.creativeScore != null ? (
                <Collapse
                  ghost
                  size="small"
                  style={{ marginBottom: 12 }}
                  items={[
                    {
                      key: "score",
                      label: (
                        <Space size={8}>
                          <span
                            style={{
                              fontWeight: 700,
                              color: scoreColor(variant.creativeScore),
                            }}
                          >
                            {variant.creativeScore.toFixed(1)}/10
                          </span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Creative score
                          </Text>
                        </Space>
                      ),
                      children: <CreativeScoreDetail detail={variant.scoreDetail} />,
                    },
                  ]}
                />
              ) : variant.scoreStatus === "error" ? (
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                  Creative score unavailable
                </Text>
              ) : null}

              <Paragraph style={{ marginBottom: 12 }}>
                {variant.rationale || "No rationale provided."}
              </Paragraph>
            </Card>
          </div>
          );
        })}
      </div>
      )}
      <Modal
        open={!!previewVariant}
        onCancel={() => setPreviewVariant(null)}
        footer={null}
        width={760}
        centered
        title={null}
      >
        {previewVariant?.stitchedVideoUrl || previewVariant?.videoUrl ? (
          <video
            src={previewVariant.stitchedVideoUrl || previewVariant.videoUrl}
            controls
            style={{ width: "100%", maxHeight: "70vh", borderRadius: 12 }}
          />
        ) : previewVariant?.imageUrl ? (
          <img
            src={previewVariant.imageUrl}
            alt="Preview"
            style={{
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 12,
            }}
          />
        ) : (
          <Text type="secondary">No preview available.</Text>
        )}
        {previewVariant?.neuroReport && (
          <div style={{ marginTop: 12 }}>
            <NeuroReport
              report={previewVariant.neuroReport}
              error={previewVariant.neuroReportError}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default VariantsGrid;
